import { z } from 'zod';

import { DateStringSchema, TimeStringSchema } from '../../common';
import { buildQueryString } from '../../utils/querystring';
import { CustomQuestionAnswerBookingResponseSchema } from '../booking/custom-question-answer';
import {
  BaseParticipantOrPurchaserSchema,
  ParticipantSchema,
  PurchaserWithCustomQuestionsSchema,
} from '../booking/participants';
import { makeEndpoint, paginateQuerySchema, paginateResponseSchema } from '../common';

export enum WaitlistEntryStatus {
  REMOVED = 'removed',
  WAITING = 'waiting',
  OFFERED = 'offered',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  EXPIRED = 'expired',
  OPENING_AVAILABLE = 'opening-available',
}

const JoinWaitlistRequestSchema = z.object({
  sessionStartDate: DateStringSchema,
  sessionStartTime: TimeStringSchema,
  sessionDuration: z.number(),
  purchaser: PurchaserWithCustomQuestionsSchema.omit({ id: true }),
  participants: z.array(ParticipantSchema).min(1),
});

const JoinWaitlistResponseSchema = z.void({});

export const JoinWaitlistEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, waitlistId }: { brandId: string; waitlistId: string }) =>
    `/v1/brands/${brandId}/waitlists/${waitlistId}/entries`,
  requestSchema: JoinWaitlistRequestSchema,
  responseSchema: JoinWaitlistResponseSchema,
});

const WaitlistParticipantSchema = BaseParticipantOrPurchaserSchema.omit({ type: true }).extend({
  id: z.string().uuid(),
  priceTierName: z.string().optional().nullable(),
  cancelledAt: z.string().datetime().nullable(),
  customQuestionAnswers: z.array(CustomQuestionAnswerBookingResponseSchema).optional(),
});

const WaitlistPurchaserSchema = BaseParticipantOrPurchaserSchema.omit({ type: true }).extend({
  id: z.string().uuid(),
  customQuestionAnswers: z.array(CustomQuestionAnswerBookingResponseSchema).optional(),
});

export const WaitlistEntrySchema = z.object({
  id: z.string().uuid(),
  status: z.nativeEnum(WaitlistEntryStatus),
  resourceGroupCollectionId: z.string().uuid(),
  listingId: z.string().uuid().nullable(),
  startDate: DateStringSchema,
  startTime: TimeStringSchema,
  sessionDuration: z.number(),
  participants: z.array(WaitlistParticipantSchema),
  purchaser: WaitlistPurchaserSchema,
  expiresAt: z.string().datetime().nullable(),
});

const GetWaitlistEntriesQuerySchema = paginateQuerySchema().extend({
  sessionDate: DateStringSchema.optional(),
  sessionStartTime: TimeStringSchema.optional(),
  filters: z
    .array(
      z.object({
        operator: z.literal('in'),
        name: z.literal('status'),
        value: z.array(z.nativeEnum(WaitlistEntryStatus)),
      }),
    )
    .optional(),
});

const GetWaitlistEntriesResponseSchema = paginateResponseSchema(WaitlistEntrySchema);

export const GetWaitlistEntriesEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    waitlistId,
    query,
  }: {
    brandId: string;
    waitlistId: string;
    query: z.input<typeof GetWaitlistEntriesQuerySchema>;
  }) => `/v1/brands/${brandId}/waitlists/${waitlistId}/entries?${buildQueryString(query)}`,
  responseSchema: GetWaitlistEntriesResponseSchema,
  requestSchema: z.void(),
  querySchema: GetWaitlistEntriesQuerySchema,
});

export const SendWaitlistInvitationOfferEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, waitlistId, waitlistEntryId }: { brandId: string; waitlistId: string; waitlistEntryId: string }) =>
    `/v1/brands/${brandId}/waitlists/${waitlistId}/entries/${waitlistEntryId}/offer-invitation`,
  requestSchema: z.void(),
  responseSchema: z.void(),
});

const OrderWaitlistEntriesRequestSchema = z.object({
  waitlistEntryIds: z.array(z.string().uuid()).min(1),
});

export const OrderWaitlistEntriesEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, waitlistId }: { brandId: string; waitlistId: string }) =>
    `/v1/brands/${brandId}/waitlists/${waitlistId}/order-waitlist-entries`,
  requestSchema: OrderWaitlistEntriesRequestSchema,
  responseSchema: z.void(),
});

export const RemoveGuestFromWaitlistEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, waitlistId, waitlistEntryId }: { brandId: string; waitlistId: string; waitlistEntryId: string }) =>
    `/v1/brands/${brandId}/waitlists/${waitlistId}/entries/${waitlistEntryId}/remove`,
  requestSchema: z.void(),
  responseSchema: z.void(),
});

export const WaitlistEntryPublicSchema = WaitlistEntrySchema.omit({}); // omit something on public ?

export const GetWaitlistEntryPublicEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, waitlistEntryId }: { brandId: string; waitlistEntryId: string }) =>
    `/v1/brands/${brandId}/waitlist-invitation-offered/${waitlistEntryId}`,
  responseSchema: WaitlistEntryPublicSchema,
  requestSchema: z.void(),
});

const ExportWaitlistEntriesQuerySchema = GetWaitlistEntriesQuerySchema.extend({
  limit: z.coerce.number().min(1).default(100),
});

export const ExportWaitlistEntriesEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    waitlistId,
    query,
  }: {
    brandId: string;
    waitlistId: string;
    query: z.input<typeof ExportWaitlistEntriesQuerySchema>;
  }) => `/v1/brands/${brandId}/waitlists/${waitlistId}/entries/export?${buildQueryString(query)}`,
  responseSchema: z.string(),
  requestSchema: z.void(),
  querySchema: ExportWaitlistEntriesQuerySchema,
});
